<template>
  <Teleport
    id="dev-CheckoutFlowModal"
    to="body"
  >
    <transition name="modal-fade">
      <div
        v-if="isOpen"
        class="modal-backdrop"
        @click="handleOverlayClick"
      >
        <div
          class="modal-content"
          :class="{ 'no-header-border': false }"
          @click.stop
        >
          <div class="modal-header">
            <h4 class="modal-title">
              {{ modalTitle }}
            </h4>
            <ModalCloseButton @close="handleClose" />
          </div>

          <div class="modal-body">
            <!-- Step 1: Cable Selection -->
            <SelectCableType
              v-if="currentStep === 'cable-selection'"
              :user-id="user ? user.id : null"
              :error-message="cableSelectionError"
              :available-cable-types="availableCableTypes"
              :initial-cable-type="selectedCableType"
              @cable-type="handleCableSelected"
            />

            <!-- Step 2: Validating Transaction -->
            <template v-if="currentStep === 'validating'">
              <div class="validation-step">
                <div class="validation-header">
                  <div class="header-icon-validating">
                    <div class="spinner-icon-container">
                      <div class="spinner-border"></div>
                    </div>
                  </div>

                  <h5 class="validation-title">
                    {{ $t('map_navigation.validating') }}
                  </h5>

                  <p class="validation-message">
                    {{ $t('map_navigation.validating_terminal') }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Step 4: Checkout/Payment -->
            <template v-if="currentStep === 'checkout'">
              <CheckoutSummary
                v-if="transactionData"
                :transaction-id="transactionData.id"
                :point-name="pointName"
                :point-id="point?.point_id"
                :token="token"
                :cable-type="selectedCableType"
                :payment-method="paymentMethod"
                :amount="transactionData.amount || 0"
                :user="updatedUser || user"
                @payment-success="handlePaymentSuccess"
                @payment-error="handlePaymentError"
                @user-updated="handleUserUpdated"
                @view-prices="showPriceTableModal = true"
                @change-cable="openCableSelector"
                @change-payment="showPaymentSelector = true"
                @cancel="handleCancel"
                @pix-payment-authorized="$emit('pix-payment-authorized', $event)"
              />
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Price Table Modal -->
  <PriceTableModal
    v-if="showPriceTableModal"
    :point-id="point?.point_id"
    @hide-price-table-modal="showPriceTableModal = false"
  />

  <!-- Payment Method Selector Overlay -->
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="showPaymentSelector"
        class="payment-selector-overlay"
        @click="cancelPaymentSelector"
      >
        <div
          class="payment-selector-modal"
          @click.stop
        >
          <div class="selector-header">
            <h5>{{ $t('map_navigation.payment_verification') }}</h5>
            <ModalCloseButton @close="cancelPaymentSelector" />
          </div>
          <div class="selector-body">
            <PaymentMethodSelector
              :user="updatedUser || user"
              @payment-method-added="handlePaymentAdded"
            />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
  import SelectCableType from '@/components/SelectCableType.vue'
  import PaymentMethodSelector from '../ui/PaymentMethodSelector.vue'
  import CheckoutSummary from '../ui/CheckoutSummary.vue'
  import PriceTableModal from '@/components/PriceTableModal.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'

  export default {
    name: 'CheckoutFlowModal',
    components: {
      SelectCableType,
      PaymentMethodSelector,
      CheckoutSummary,
      PriceTableModal,
      ModalCloseButton
    },
    props: {
      isOpen: {
        type: Boolean,
        default: false
      },
      point: {
        type: Object,
        default: null
      },
      user: {
        type: Object,
        required: true
      },
      pendingPix: {
        type: Object,
        default: null
      },
      initialCableType: {
        type: Number,
        default: null
      }
    },
    emits: ['close', 'start-rental', 'cable-changed', 'validate-transaction', 'error', 'pix-payment-authorized'],
    data() {
      return {
        currentStep: 'cable-selection', // 'cable-selection', 'validating', 'checkout'
        selectedCableType: null,
        paymentMethod: null,
        updatedUser: null,
        transactionData: null,
        cableSelectionError: '',
        availableCableTypes: null,
        showPriceTableModal: false,
        showPaymentSelector: false,
        validationTimeoutId: null
      }
    },
    computed: {
      modalTitle() {
        switch (this.currentStep) {
          case 'cable-selection':
            return this.$t('map_navigation.select_cable')
          case 'validating':
            return this.$t('map_navigation.validating')
          case 'checkout':
            return this.$t('map_navigation.checkout_verify_title')
          default:
            return ''
        }
      },
      pointName() {
        return this.point?.name || ''
      },
      token() {
        return this.point?.token || ''
      }
    },
    watch: {
      isOpen(newValue) {
        if (newValue) {
          lockScroll()
          this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_OPENED)
          if (this.pendingPix) {
            this._restoreFromPendingPix()
          } else {
            this.startFlow()
          }
        } else {
          unlockScroll()
          // Clean up internal state when modal closes via any path
          // (close button, MapNavigation.closeCheckoutModal, etc.)
          this.resetFlow()
        }
      },
      currentStep(newStep) {
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_STEP_CHANGED, { step: newStep })
      }
    },
    beforeUnmount() {
      if (this.isOpen) {
        unlockScroll()
      }
      // Clean up any pending timeouts
      if (this.validationTimeoutId) {
        clearTimeout(this.validationTimeoutId)
        this.validationTimeoutId = null
      }
    },
    methods: {
      trackEvent(eventName, extraData = {}) {
        try {
          const transactionId = extraData.transaction_id || this.transactionData?.id || null
          const event = new UserEvent(eventName, transactionId, {
            point_id: this.point?.point_id,
            point_name: this.point?.name,
            ...extraData
          })
          sessionRecorder.addUserEvent(event)
        } catch (error) {
          console.warn('[CheckoutFlowModal] Error tracking event:', error)
        }
      },

      handleCableSelected(cableType) {
        console.log('[CheckoutFlowModal] handleCableSelected:', cableType)
        this.selectedCableType = cableType
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_CABLE_SELECTED, { cable_type: cableType })
        this.cableSelectionError = '' // Clear error on new selection
        this.$emit('cable-changed', cableType)

        // If we are already in the middle of a flow (e.g. changing from summary),
        // we need to re-validate to get new transaction data
        // We use nextTick to ensure the parent (home-page.js) has updated its this.cableType
        this.$nextTick(() => {
          // If we already had a transaction, we definitely need to re-validate
          // to get a new transaction ID for the new cable
          if (this.transactionData) {
            this.validateTransaction()
          } else {
            this.checkPaymentMethod()
          }
        })
      },

      checkPaymentMethod() {
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_PAYMENT_CHECK)
        const hasCardPayment = Boolean(this.user?.default_card)
        const hasPixPayment = Boolean(
          this.user?.pix_identification?.name &&
          this.user?.pix_identification?.cpf_or_cnpj
        )

        if (hasCardPayment || hasPixPayment) {
          this.paymentMethod = hasCardPayment ? 'card' : 'pix'
          this.validateTransaction()
        } else {
          this.currentStep = 'validating'
          this.showPaymentSelector = true
        }
      },

      handlePaymentAdded(paymentData) {
        if (!paymentData) return
        this.showPaymentSelector = false
        this.paymentMethod = paymentData.type
        this.updatedUser = paymentData.user || this.updatedUser
        // Use nextTick to ensure overlay is fully removed from DOM before validation
        this.$nextTick(() => {
          this.validateTransaction()
        })
      },

      cancelPaymentSelector() {
        this.showPaymentSelector = false

        // If we don't have a transaction yet, cancelling the payment step
        // should close the checkout flow and return to the station dialog.
        if (!this.transactionData) {
          this.handleClose()
        }
      },

      handleUserUpdated(updatedUser) {
        if (!updatedUser) return
        this.updatedUser = updatedUser
      },

      validateTransaction() {
        if (!this.point) return
        this.currentStep = 'validating'
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_VALIDATION_STARTED)

        // Set timeout to prevent spinner from freezing indefinitely (30 seconds)
        this.validationTimeoutId = setTimeout(() => {
          if (this.currentStep === 'validating') {
            console.error('[CheckoutFlowModal] Validation timeout - no response from server')
            this.onValidationError({
              error: this.$t('map_navigation.validation_timeout') || 'Validation timeout - please try again',
              code: 'VALIDATION_TIMEOUT'
            })
          }
        }, 30000)

        this.$emit('validate-transaction', {
          token: this.point.token,
          pointName: this.point.name,
          pointId: this.point.point_id,
          cableType: this.selectedCableType,
          updatedUser: this.updatedUser,
          paymentMethod: this.paymentMethod
        })
      },

      onValidationSuccess(transactionData) {
        console.log('[CheckoutFlowModal] onValidationSuccess called', transactionData)
        // Clear validation timeout
        if (this.validationTimeoutId) {
          clearTimeout(this.validationTimeoutId)
          this.validationTimeoutId = null
        }
        this.transactionData = transactionData
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_VALIDATION_SUCCESS, {
          transaction_id: transactionData.id,
          amount: transactionData.amount
        })
        this.currentStep = 'checkout'
        console.log('[CheckoutFlowModal] Current step set to:', this.currentStep)
      },

      onValidationError(errorData) {
        console.log('[CheckoutFlowModal] onValidationError called', errorData)
        if (this.validationTimeoutId) {
          clearTimeout(this.validationTimeoutId)
          this.validationTimeoutId = null
        }
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_VALIDATION_ERROR, { error: errorData })
        if (!errorData) {
          this.resetFlow()
          return false
        }

        if (errorData.code === 1004 || errorData.code === 'NO_BATTERY_WITH_CABLE') {
          // 1004 = NO_BATTERY_WITH_CABLE
          this.cableSelectionError = errorData.error || this.$t('map_navigation.error_no_cable_message')
          if (errorData.cable_type_available && Array.isArray(errorData.cable_type_available)) {
            const availableSet = new Set()
            errorData.cable_type_available.forEach(batteryType => {
              if (batteryType === 0) {
                availableSet.add(0) // Lightning
                availableSet.add(1) // Micro
              } else if (batteryType === 1) {
                availableSet.add(0) // Lightning
                availableSet.add(2) // Type C
              }
            })
            this.availableCableTypes = Array.from(availableSet)
            console.log('[CheckoutFlowModal] Updated availableCableTypes:', this.availableCableTypes)
          }

          this.currentStep = 'cable-selection'
          return true // Handled internally
        }

        // For other errors (terminal offline, no charged battery, generic, etc.),
        // keep the modal on the current step (validating) instead of switching to cable-selection.
        // The parent (MapNavigation) will show ValidationErrorDialog on top,
        // and handleSecondaryErrorAction will close the checkout modal when needed.
        return false // Not handled internally, let parent show dialog
      },

      handlePaymentSuccess(paymentData) {
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_PAYMENT_SUCCESS, paymentData)
        this.$emit('start-rental', {
          ...paymentData,
          point: this.point,
          cableType: this.selectedCableType
        })
        this.resetFlow()
        this.$emit('close')
      },

      handlePaymentError(error) {
        console.error('Payment error:', error)
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_PAYMENT_ERROR, { error })

        // Use translated error message instead of raw text
        const errorMessage =
          error.error || this.$t('map_navigation.payment_error_message') || 'An error occurred during payment.'

        this.$emit('error', {
          error: errorMessage,
          code: error.code || 'PAYMENT_ERROR',
          details: error
        })
        // Do NOT close modal, just stay on checkout step so user can try again or see summary
        this.currentStep = 'checkout'
      },

      handleCancel() {
        console.log('[CheckoutFlowModal] handleCancel called')
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_CANCELED, { step: this.currentStep })
        // If we are in cable selection but already have a transaction (came from checkout summary),
        // go back to checkout instead of closing the whole flow
        if (this.currentStep === 'cable-selection' && this.transactionData) {
          this.currentStep = 'checkout'
          return
        }
        this.resetFlow()
        this.$emit('close')
      },

      handleClose() {
        this.trackEvent(SESSION_EVENTS.CHECKOUT_FLOW_CLOSED)
        this.handleCancel()
      },

      openPaymentSelector() {
        console.log('[CheckoutFlowModal] openPaymentSelector called')
        this.showPaymentSelector = true
      },
      openCableSelector() {
        console.log('[CheckoutFlowModal] openCableSelector called')
        this.showPaymentSelector = false
        this.cableSelectionError = ''
        this.availableCableTypes = null
        this.selectedCableType = null
        this.transactionData = null
        this.currentStep = 'cable-selection'
      },
      handleOverlayClick() {
        // Optional: Close modal when clicking outside
        // this.handleClose()
      },

      resetFlow() {
        console.log('[CheckoutFlowModal] resetFlow called')
        this.currentStep = 'cable-selection'
        this.selectedCableType = null
        this.paymentMethod = null
        this.updatedUser = null
        this.transactionData = null
        this.cableSelectionError = ''
        this.availableCableTypes = null
        this.showPriceTableModal = false
      },

      _restoreFromPendingPix() {
        this.selectedCableType = this.pendingPix.cableType
        this.paymentMethod = 'pix'
        this.transactionData = { id: this.pendingPix.transactionId, amount: this.pendingPix.amount }
        if (this.selectedCableType === null || this.selectedCableType === undefined) {
          this.currentStep = 'cable-selection'
        } else {
          this.currentStep = 'checkout'
        }
      },

      startFlow() {
        const presetCable =
          this.initialCableType !== null
            ? this.initialCableType
            : this.user?.cable_type !== null && this.user?.cable_type !== undefined
              ? this.user?.cable_type
              : null

        // Use retained selectedCableType (from a previous failed attempt),
        // falling back to presetCable from user preferences or props.
        // Only show cable selection if NEITHER is available.
        const cableToUse =
          this.selectedCableType !== null && this.selectedCableType !== undefined ? this.selectedCableType : presetCable

        if (cableToUse !== null && cableToUse !== undefined) {
          this.selectedCableType = cableToUse
          this.checkPaymentMethod()
          return
        }
        this.currentStep = 'cable-selection'
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .modal-backdrop {
    position: fixed;
    top: var(--app-fixed-top-offset, 0px);
    left: 0;
    right: 0;
    height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px));
    height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px));
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-end; /* Mobile-first: sheet from bottom */
    justify-content: center;
    z-index: 11000; /* Higher than ManualTokenInputDialog (10000) */
    padding: 0;
  }

  .modal-content {
    background: #ffffff;
    border-radius: 20px 20px 0 0; /* Rounded top for bottom sheet */
    max-width: 600px;
    width: 100%;
    max-height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 16px);
    max-height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 16px);
    bottom: 0;
    padding: 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    animation: slide-up 0.3s cubic-bezier(0.175, 0.885, 0.32, 1);
    position: relative;
    overflow: hidden; /* Header and handle stay fixed at top */

    /* Pull handle indicator */
    &::before {
      content: '';
      width: 40px;
      height: 4px;
      background-color: #ddd;
      border-radius: 2px;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }

    &.no-header-border {
      .modal-header {
        border-bottom: none;
      }
    }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1rem 0.75rem; /* Added top padding for pull handle */
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
    background: white;
    position: relative;
    z-index: 10;
  }

  .modal-title {
    font-size: 0.9rem; /* Reduced from 0.95rem */
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
    text-align: center;
    padding: 0 3rem; /* Reduced from 3.5rem */
    width: 100%;
    line-height: 1.3;
  }

  .modal-body {
    padding: 1.25rem; /* Reduced from 1.5rem */
    overflow-y: auto;
    flex: 1;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  // Payment step styling
  .payment-step {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .payment-header {
    text-align: center;
    margin-bottom: 1.5rem; /* Reduced from 2rem */
    width: 100%;
  }

  .header-icon-payment {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem; /* Reduced from 4rem */
    height: 3.5rem;
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    border-radius: 50%;
    box-shadow: 0 6px 16px rgba(40, 167, 69, 0.15); /* Reduced shadow */
    margin-bottom: 1rem; /* Reduced from 1.25rem */
  }

  .icon-payment {
    font-size: 1.75rem; /* Reduced from 2rem */
    color: #28a745;
  }

  .payment-title {
    font-size: 1.15rem; /* Reduced from 1.25rem */
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 0.4rem; /* Reduced from 0.5rem */
  }

  .payment-message {
    font-size: 0.9rem; /* Reduced from 0.95rem */
    color: #5a6c7d;
    line-height: 1.4;
    margin: 0;
  }

  // Validation step styling
  .validation-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem; /* Reduced from 3rem */
    min-height: 240px; /* Reduced from 300px */
  }

  .validation-header {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .header-icon-validating {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 4rem; /* Reduced from 5rem */
    height: 4rem;
    background: white;
    border-radius: 50%;
    box-shadow: 0 0 0 6px rgba(245, 138, 72, 0.1); /* Reduced ring size */
    margin-bottom: 1.5rem; /* Reduced from 2rem */
    position: relative;
  }

  .spinner-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .spinner-border {
    width: 2rem; /* Reduced from 2.5rem */
    height: 2rem;
    border-width: 2.5px; /* Reduced from 3px */
    color: #f58a48;
  }

  .validation-title {
    font-size: 1.25rem; /* Reduced from 1.5rem */
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 0.6rem; /* Reduced from 0.75rem */
  }

  .validation-message {
    font-size: 0.9rem; /* Reduced from 1rem */
    color: #7f8c8d;
    line-height: 1.4;
    margin: 0;
  }

  // Button utilities
  .btn-block {
    width: 100%;
  }

  .mt-3 {
    margin-top: 1rem;
  }

  // Responsive
  @media (max-width: 766px) {
    .modal-backdrop {
      align-items: flex-end; // Sheet from bottom on mobile
    }

    .modal-content {
      position: fixed;
      // bottom: var(--app-fixed-bottom-offset, 0px);
      max-width: 100%;
      border-radius: 20px 20px 0 0;
      max-height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 16px);
      max-height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 16px);
    }

    .modal-header {
      padding: 1.25rem 1rem 0.75rem;
      border-bottom: 1px solid #f5f5f5;
    }

    .modal-title {
      font-size: 1rem;
    }

    .modal-body {
      padding: 0 1.25rem 2rem 1.25rem;
    }
  }

  .payment-selector-overlay {
    position: fixed;
    top: var(--app-fixed-top-offset, 0px);
    bottom: var(--app-fixed-bottom-offset, 0px);
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    z-index: 12000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .payment-selector-modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 450px;
    max-height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 32px);
    max-height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 32px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modal-fade-in 0.3s ease-out;
  }

  .selector-header {
    padding: 1rem;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;

    h5 {
      margin: 0;
      font-size: 1rem;
      font-weight: 700;
      color: #2c3e50;
    }
  }

  .selector-body {
    flex: 1;
    min-height: 0;
    padding: 1.25rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  @keyframes modal-fade-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 576px) {
    .payment-selector-modal {
      max-height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 32px);
      max-height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 32px);
      max-width: 100%;
      border-radius: 16px 16px 0 0;
      position: absolute;
      bottom: 0;
    }
  }
</style>
